import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Link,
} from '@material-ui/core';
import { Error, ExpandMore } from '@material-ui/icons';
import {
  LoadInspectionType,
  LoadVehicleInspectionDTO,
  LooseItemsDTO,
} from '../data/LoadDTO';
import { Column, Columns, Inline, Stack } from '@superdispatch/ui';
import { joinStrings } from 'shared/utils/StringUtils';
import { useMemo } from 'react';
import { chooseUTMSource } from 'shared/helpers/Analytics';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import { useFlag } from 'shared/settings/FeatureToggles';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { parseURITemplate } from '@superdispatch/uri';
import styled from 'styled-components';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { ViewLoadAIAGInspectionDamages } from './view-load-inspection/ViewLoadAIAGInspectionDamages';
import { ViewLoadInspectionAdditionalMedia } from './view-load-inspection/ViewLoadInspectionAdditionalMedia';
import {
  ViewLoadInspectionLooseItems,
  hasLooseItem,
} from './view-load-inspection/ViewLoadInspectionLooseItems';
import { ViewLoadInspectionNoDamages } from './view-load-inspection/ViewLoadInspectionNoDamages';
import {
  getVehicleInspectionPhotoSyncCounts,
  shouldShowVehicleInspectionSyncSummary,
} from './loadInspectionSyncFromLoad';
import { ViewLoadVehicleInspectionSyncSummary } from './view-load-inspection/ViewLoadVehicleInspectionSyncSummary';

const AccordionSummaryBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

interface Props {
  expanded?: boolean;
  type: LoadInspectionType;
  inspection: LoadVehicleInspectionDTO;
  bolUrl: string;
  loadStage: string;
}

function countDamagedPhotos(photos?: Array<{ damages?: unknown[] }>) {
  if (!photos?.length) return 0;
  return photos.reduce((acc, photo) => acc + (photo.damages?.length || 0), 0);
}

interface InspectionData {
  photos: LoadVehicleInspectionDTO['photos'];
  videos: LoadVehicleInspectionDTO['videos'];
  aiagInspection?: LoadVehicleInspectionDTO['aiag_inspections'][number];
  additionalInspection?: LooseItemsDTO;
  inspectionNotes?: string | null;
}

function getInspectionDataByStep(
  inspection: LoadVehicleInspectionDTO,
  step: 'pickup' | 'delivery',
): InspectionData {
  const photoType = step === 'pickup' ? 'Pickup' : 'Delivery';
  return {
    photos: inspection.photos?.filter(({ type }) => type === photoType) || [],
    videos: inspection.videos?.filter(({ type }) => type === photoType) || [],
    aiagInspection: inspection.aiag_inspections.find(
      ({ step: s }) => s === step,
    ),
    additionalInspection: inspection.additional_inspections?.find(
      ({ type }) => type === step,
    ),
    inspectionNotes:
      step === 'pickup'
        ? inspection.pickup_inspection_notes
        : inspection.delivery_inspection_notes,
  };
}

function hasInspection(data: InspectionData): boolean {
  return (
    !!data.photos?.length ||
    !!data.videos?.length ||
    !!data.aiagInspection ||
    !!data.additionalInspection
  );
}

interface NonVehiclePhotoWarningProps {
  count: number;
  bolUrl: string;
  loadStage: string;
}

function NonVehiclePhotoWarning({
  count,
  bolUrl,
  loadStage,
}: NonVehiclePhotoWarningProps) {
  return (
    <Box
      backgroundColor="Red50"
      display="inline-block"
      paddingLeft="xxsmall"
      paddingRight="xxsmall"
      borderRadius="small"
    >
      <Inline verticalAlign="center" space="xxsmall">
        <Error fontSize="small" color="error" />
        <Link
          href={parseURITemplate(
            `${bolUrl}{?utm_source,utm_medium,utm_content}`,
            {
              utm_source: chooseUTMSource(),
              utm_medium: 'View Load Page',
              utm_content: 'Non-vehicle Photo Issues Link',
            },
          )}
          onClick={() => {
            trackLoadsEvent({
              name: 'Carrier Clicked Non-vehicle Photo Issues',
              page: 'view_load',
              utm_medium: loadStage,
            });
          }}
        >
          {count} Inspection photo {formatPlural(count, 'issue', 'issues')}
        </Link>
      </Inline>
    </Box>
  );
}

interface InspectionSectionProps {
  type: 'pickup' | 'delivery';
  photos?: LoadVehicleInspectionDTO['photos'];
  videos?: LoadVehicleInspectionDTO['videos'];
  aiagInspection?: LoadVehicleInspectionDTO['aiag_inspections'][number];
  additionalInspection?: LooseItemsDTO;
  inspectionNotes?: string | null;
  make: string;
  model: string;
}

function InspectionSection({
  type,
  photos = [],
  videos = [],
  aiagInspection,
  additionalInspection,
  inspectionNotes,
  make,
  model,
}: InspectionSectionProps) {
  const { type: additionalInspectionType, ...looseItems } =
    additionalInspection ?? {};
  const hasAdditionalInspectionOrNotes =
    !!hasLooseItem(looseItems) || !!inspectionNotes;

  const hasPhotosOrVideos = !!photos.length || !!videos.length;

  return (
    <Stack space="medium">
      <TextBox variant="heading-4">
        {type === 'pickup' ? 'Pickup' : 'Delivery'} Inspections
      </TextBox>

      {aiagInspection?.damages?.length === 0 && <ViewLoadInspectionNoDamages />}

      {aiagInspection && (
        <ViewLoadAIAGInspectionDamages inspection={aiagInspection} />
      )}

      <Columns collapseBelow="tablet" space="medium">
        {hasPhotosOrVideos && (
          <Column width={hasAdditionalInspectionOrNotes ? '1/2' : 'fluid'}>
            <ViewLoadInspectionAdditionalMedia
              make={make}
              model={model}
              photos={photos}
              videos={videos}
              type={type}
              direction={
                hasAdditionalInspectionOrNotes ? 'vertical' : 'horizontal'
              }
            />
          </Column>
        )}

        {hasAdditionalInspectionOrNotes && (
          <Column width={hasPhotosOrVideos ? '1/2' : 'fluid'}>
            <Stack>
              <TextBox variant="heading-5">Additional Inspection</TextBox>
              {inspectionNotes && (
                <Box
                  backgroundColor="Blue50"
                  padding="xsmall"
                  borderRadius="small"
                >
                  {inspectionNotes}
                </Box>
              )}
              {additionalInspection && (
                <ViewLoadInspectionLooseItems
                  looseItems={additionalInspection}
                />
              )}
            </Stack>
          </Column>
        )}
      </Columns>
    </Stack>
  );
}

export function ViewLoadVehicleInspection({
  expanded,
  inspection,
  loadStage,
  bolUrl,
}: Props) {
  const isPhotoSyncEnabled = useFlag('ctms_photo_sync');
  const { year, make, model, vin, photos } = inspection;

  const undetectedVehiclePhotos = photos?.filter(
    (p) => p.is_vehicle_detected === false,
  );

  const pickupData = useMemo(
    () => getInspectionDataByStep(inspection, 'pickup'),
    [inspection],
  );
  const deliveryData = useMemo(
    () => getInspectionDataByStep(inspection, 'delivery'),
    [inspection],
  );

  const damagedPhotosCount =
    countDamagedPhotos(pickupData.photos) +
    countDamagedPhotos(deliveryData.photos);

  const inspectionSections = useMemo(() => {
    const sections = [
      { type: 'pickup' as const, data: pickupData },
      { type: 'delivery' as const, data: deliveryData },
    ];
    return sections.filter(({ data }) => hasInspection(data));
  }, [pickupData, deliveryData]);

  const vehiclePhotoSyncCounts = useMemo(() => {
    if (!isPhotoSyncEnabled) {
      return null;
    }
    if (!shouldShowVehicleInspectionSyncSummary(inspection)) {
      return null;
    }
    return getVehicleInspectionPhotoSyncCounts(inspection);
  }, [inspection, isPhotoSyncEnabled]);

  return (
    <Accordion defaultExpanded={expanded}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="inspection-content"
        id="inspection-header"
      >
        <AccordionSummaryBody>
          <Inline space="xsmall" verticalAlign="center">
            <TextBox variant="body-semibold" color="secondary">
              {joinStrings(' ', year, make, model)}
            </TextBox>
            <TextBox color="secondary">{vin}</TextBox>
            {damagedPhotosCount > 0 && (
              <TextBox color="secondary">
                · {damagedPhotosCount}{' '}
                {formatPlural(damagedPhotosCount, 'Damage', 'Damages')}
              </TextBox>
            )}

            {!!undetectedVehiclePhotos?.length && (
              <NonVehiclePhotoWarning
                count={undetectedVehiclePhotos.length}
                bolUrl={bolUrl}
                loadStage={loadStage}
              />
            )}
          </Inline>
          {vehiclePhotoSyncCounts && (
            <ViewLoadVehicleInspectionSyncSummary
              synced={vehiclePhotoSyncCounts.synced}
              total={vehiclePhotoSyncCounts.total}
            />
          )}
        </AccordionSummaryBody>
      </AccordionSummary>
      <AccordionDetails>
        <Stack space="medium">
          {inspectionSections.map(({ type, data }, index) => (
            <Stack space="medium" key={type}>
              {index > 0 && <Divider />}
              <InspectionSection
                type={type}
                photos={data.photos}
                videos={data.videos}
                aiagInspection={data.aiagInspection}
                additionalInspection={data.additionalInspection}
                inspectionNotes={data.inspectionNotes ?? undefined}
                make={make}
                model={model}
              />
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
